import React from 'react';
import { round } from 'lodash';
import { AllocationRule } from 'further-types/lp/allocations';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { useFormContext } from 'react-hook-form';
import { EqualisationFormValues } from './FormWrapper';
import { numberToCurrencyString, dateToLabel } from 'further-ui/utils';
import useEqualiseActions from '../../hooks/useEqualiseActions';
import EqualisationsAllocationsTable, {
  ColumnConfig,
} from 'components/EqualisationElements/EqualisationsAllocationsTable';
import NumericInputCell from 'components/EqualisationElements/NumericInputCell';
import CheckboxCell from 'components/EqualisationElements/CheckboxCell';

type EqualisationAllocationsFormProps = {
  fields: EqualisationFormValues['actions'][number]['newAllocations'];
  inputAmountLcy: number;
  isNotEditable: boolean;
  allocationRule: AllocationRule;
  actionIndex: number;
  allocationType: 'originalAllocations' | 'newAllocations';
  linkedActionIndex?: number;
  transactionCurrency: CurrencyCode;
};

const EqualisationAllocationsForm: React.FC<
  EqualisationAllocationsFormProps
> = ({
  fields,
  allocationRule,
  inputAmountLcy,
  isNotEditable,
  actionIndex,
  allocationType,
  linkedActionIndex,
  transactionCurrency,
}) => {
  const { control } = useFormContext<EqualisationFormValues>();

  const {
    watchedFields,
    isPending,
    allocationsPath,
    totalAllocationAmountLcy,
    totalAllocationPercentage,
    excludedCommitmentNames,
    onChangeAllocationRule,
    handleAllocationAmountChange,
    handleAllocationPercentageChange,
    handleExcludeFromAllocationChange,
    isLinkedAction,
  } = useEqualiseActions({
    actionIndex,
    linkedActionIndex,
    allocationType,
    inputAmountLcy,
  });

  const columns: Array<ColumnConfig> = [
    {
      header: 'Investor',
      renderCell: (row) => row.investorLabel,
    },
    {
      header: 'Total commitment',
      renderCell: (row) => numberToCurrencyString(row.totalCommitmentLcy),
    },
    {
      header: 'Unallocated commitment',
      renderCell: (row) => numberToCurrencyString(row.unallocatedCommitmentLcy),
    },
    {
      header: 'Close date',
      renderCell: (row) => dateToLabel(row.closeDate),
    },
    {
      header: 'Allocation allocated',
      renderCell: (row, rowIndex) => (
        <NumericInputCell
          fixedDecimalScale={true}
          control={control}
          name={`${allocationsPath}.${rowIndex}.allocationAmountLcy`}
          onChange={(value) => handleAllocationAmountChange(value, rowIndex)}
          currency={transactionCurrency}
          disabled={isNotEditable || row.excludeFromAllocation || isPending}
        />
      ),
      renderFooter: () =>
        totalAllocationAmountLcy.toLocaleString('en-US', {
          style: 'currency',
          currency: transactionCurrency,
        }),
    },
    {
      header: 'Allocation allocated (%)',
      renderCell: (row, rowIndex) => (
        <NumericInputCell
          decimalScale={5}
          fixedDecimalScale={true}
          control={control}
          name={`${allocationsPath}.${rowIndex}.allocationPercentage`}
          onChange={(value) =>
            handleAllocationPercentageChange(value, rowIndex)
          }
          disabled={isNotEditable || row.excludeFromAllocation || isPending}
        />
      ),
      renderFooter: () => `${round(totalAllocationPercentage, 5)}%`,
    },
    {
      header: 'Exclude investor from allocation',
      renderCell: (_row, rowIndex) => (
        <CheckboxCell
          control={control}
          name={`${allocationsPath}.${rowIndex}.excludeFromAllocation`}
          onChange={(value) =>
            handleExcludeFromAllocationChange(value, rowIndex, allocationRule)
          }
          disabled={isNotEditable}
        />
      ),
    },
  ];

  const rows = fields.map((field) => ({
    ...field,
    key: field.commitmentId,
  }));

  return (
    <EqualisationsAllocationsTable
      columns={columns}
      rows={rows}
      footerLabel={`${watchedFields?.length || 0} investors`}
      isPending={isPending}
      showAllocationRule={!isNotEditable}
      allocationRule={allocationRule}
      allocationRuleOptions={[
        {
          value: AllocationRule.ByCommitmentAndClosingDate,
          label: `By commitment and closing date${
            excludedCommitmentNames
              ? ` excluding ${excludedCommitmentNames}`
              : ''
          }`,
        },
        {
          value: AllocationRule.Manual,
          label: 'Manual allocation',
        },
        ...(isLinkedAction
          ? [
              {
                value: AllocationRule.LinkedAction,
                label: 'Linked action',
              },
            ]
          : []),
      ]}
      onChangeAllocationRule={onChangeAllocationRule}
      isNotEditable={isNotEditable}
    />
  );
};

export default EqualisationAllocationsForm;
